import React, { useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Check, X, RotateCw, Maximize2 } from "lucide-react";

/**
 * Free-form crop modal — the FULL invoice is always visible and the user drags
 * a resizable rectangle (8 handles) over it. Rotation supported.
 *
 * Props:
 *  - image: dataUrl string
 *  - onConfirm(croppedDataUrl)
 *  - onCancel()
 */
export default function CropModal({ image, onConfirm, onCancel }) {
  const [src, setSrc] = useState(image);
  const [crop, setCrop] = useState({ unit: "%", x: 2, y: 2, width: 96, height: 96 });
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);

  const selectAll = () => setCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 });

  const rotate = async () => {
    setBusy(true);
    try {
      const rotated = await rotate90(src);
      setSrc(rotated);
      setCrop({ unit: "%", x: 2, y: 2, width: 96, height: 96 });
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!crop || !crop.width || !crop.height) {
      onConfirm(src);
      return;
    }
    setBusy(true);
    try {
      const cropped = await cropByPercent(src, crop);
      onConfirm(cropped);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="crop-modal" dir="rtl">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between bg-[#1A1C19] text-white shrink-0">
        <div className="font-readex font-medium">اقتصاص حر — اسحب الزوايا بحرية</div>
        <button onClick={onCancel} className="p-1.5 -me-1.5" data-testid="crop-close" aria-label="إغلاق">
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Crop area — the whole invoice is visible, scrollable if very tall */}
      <div className="relative flex-1 overflow-auto bg-black flex items-center justify-center p-3">
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          keepSelection
          ruleOfThirds
          className="max-w-full"
        >
          <img
            ref={imgRef}
            src={src}
            alt="فاتورة"
            style={{ maxHeight: "62vh", maxWidth: "100%", width: "auto", display: "block" }}
            data-testid="crop-image"
          />
        </ReactCrop>
        <div className="absolute top-2 inset-x-2 pointer-events-none text-center">
          <span className="inline-block bg-black/60 text-white text-[11px] font-readex px-3 py-1.5 rounded-full backdrop-blur-sm">
            الفاتورة معروضة بالكامل · اسحب أي زاوية أو حافة لتحديد منطقة القص
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-[#1A1C19] text-white px-4 py-4 space-y-3 shrink-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={rotate}
            disabled={busy}
            data-testid="crop-rotate"
            className="bg-white/10 hover:bg-white/15 text-white rounded-md px-4 py-2.5 font-medium font-readex inline-flex items-center gap-2 text-sm disabled:opacity-60"
          >
            <RotateCw className="w-4 h-4" strokeWidth={1.5} />
            تدوير 90°
          </button>
          <button
            type="button"
            onClick={selectAll}
            disabled={busy}
            data-testid="crop-select-all"
            className="bg-white/10 hover:bg-white/15 text-white rounded-md px-4 py-2.5 font-medium font-readex inline-flex items-center gap-2 text-sm disabled:opacity-60"
          >
            <Maximize2 className="w-4 h-4" strokeWidth={1.5} />
            تحديد الكل
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="crop-cancel-btn"
            className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-md px-5 py-3 font-medium font-readex disabled:opacity-60"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            data-testid="crop-confirm-btn"
            className="bg-[#1F3A33] hover:bg-[#172A25] text-white rounded-md px-5 py-3 font-medium font-readex inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Check className="w-5 h-5" strokeWidth={1.5} />
            {busy ? "جاري…" : "تأكيد القص"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------- helpers -----------------------

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = url;
  });
}

async function rotate90(imageSrc) {
  const img = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = img.height;
  canvas.height = img.width;
  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);
  return canvas.toDataURL("image/jpeg", 0.95);
}

async function cropByPercent(imageSrc, percentCrop) {
  const img = await createImage(imageSrc);
  const x = Math.round((percentCrop.x / 100) * img.width);
  const y = Math.round((percentCrop.y / 100) * img.height);
  const w = Math.max(1, Math.round((percentCrop.width / 100) * img.width));
  const h = Math.max(1, Math.round((percentCrop.height / 100) * img.height));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.92);
}
